/* eslint-disable */
import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { useTranslation } from "react-i18next";

import { getData, uploadSingleFile, postData, putData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

import FileUploadField from "components/Common/FileUploadField/FileUploadField";
import { getCustomStyles, reactSelectStyle } from "Config/Config";
import { assetImages } from "constants";
import PatientDetailsCard from "../PatientDetailsCard/PatientDetailsCard";
import DoctorDetailsCard from "../DoctorDetailsCard/DoctorDetailsCard";
import ClinicDetailsCard from "../ClinicDetailsCard/ClinicDetailsCard";

const SavePrescriptionPopup = ({
  selectedPrescriptionId,
  setSelectedPrescriptionId,
  afterActionClose,
  patientObj = null,
  doctorObj = null,
  clinicObj = null,
}) => {
  const { t } = useTranslation();
  const token = localStorage.getItem("token");
  const todayValue = new Date().toISOString().split("T")[0];

  // Form state
  const [documentName, setDocumentName] = useState("");
  const [docFileUploading, setDocFileUploading] = useState(false);
  const [fileUploadId, setFileUploadId] = useState(null);
  const [uploadedFile, setUploadedFile] = useState({});
  const [testDate, setTestDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [resultDate, setResultDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [symptoms, setSymptoms] = useState("");
  const [suggestions, setSuggestions] = useState("");
  const [comments, setComments] = useState("");
  const [assignTo, setAssignTo] = useState(null);
  const [doctor, setDoctor] = useState(null);
  const [clinic, setClinic] = useState(null);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  // Disease section states for form and table
  const [diseaseForm, setDiseaseForm] = useState({
    disease: "",
    name: "",
    severity: "",
    duration: "",
    description: "",
  });
  const [diseasesList, setDiseasesList] = useState([]);
  const [editDiseaseIdx, setEditDiseaseIdx] = useState(null);

  // Medication section states for form and table
  const [medicationForm, setMedicationForm] = useState({
    medicine: null,
    medicinevalue: null,
    name: "",
    dose: "",
    frequency: "",
    duration: "",
    purpose: "",
  });
  const [medicationsList, setMedicationsList] = useState([]);
  const [editMedicationIdx, setEditMedicationIdx] = useState(null);

  // Laboratory test section states for form and table
  const [labTestForm, setLabTestForm] = useState({
    medicaltest: null,
    medicaltestvalue: null,
    name: "",
    result: "",
    date: todayValue,
  });
  const [laboratoryTestList, setLaboratoryTestList] = useState([]);
  const [editLabTestIdx, setEditLabTestIdx] = useState(null);

  const formRef = useRef(null);

  // Form validation
  const validateForm = () => {
    const newErrors = {};
    if (!documentName.trim())
      newErrors.documentName = "Document Name is required.";
    if (!testDate) newErrors.testDate = "Test Date is required.";
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0 && formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    return Object.keys(newErrors).length === 0;
  };

  // Async select options
  const loadAssineeOptions = async (inputValue) => {
    try {
      let requestUrl =
        url.API_BASE_URL +
        url.API_GET_USER_LIST_BY_USER +
        `?token=${token}&userstringinput=${inputValue}&modulefrom=backend&filterroleslug=PATIENT`;

      const reponse = await getData(requestUrl);
      return reponse.data;
    } catch (error) {
      return [];
    }
  };

  const loadDoctorOptions = async (inputValue) => {
    try {
      let requestUrl =
        url.API_BASE_URL +
        url.API_GET_USER_LIST_BY_USER +
        `?token=${token}&userstringinput=${inputValue}&modulefrom=backend&filterroleslug=DOCTOR`;

      const reponse = await getData(requestUrl);
      return reponse.data;
    } catch (error) {
      return [];
    }
  };

  const loadClinicOptions = async (inputValue) => {
    try {
      let requestUrl =
        url.API_BASE_URL +
        url.API_GET_ALL_COMPANY_LIST +
        `?token=${token}&filtername=${inputValue}`;
      const response = await getData(requestUrl);
      return response.data;
    } catch (error) {
      return [];
    }
  };

  const loadCurrentStatusOptions = async (inputValue) => {
    try {
      const requestUrl = url.SERVER_URL + url.CONDITION_STATUS_OPTIONS;
      const response = await getData(requestUrl);
      let options = Array.isArray(response)
        ? response
        : Array.isArray(response.data)
        ? response.data
        : [];
      if (inputValue) {
        options = options.filter((opt) =>
          opt.label?.toLowerCase().includes(inputValue.toLowerCase())
        );
      }
      return options.map((opt) => ({
        label: opt.label,
        value: opt.value,
      }));
    } catch (error) {
      return [];
    }
  };

  //load disease options
  const loadDiseaseOptions = async () => {
    try {
      const requestUrl = url.SERVER_URL + url.MEDICAL_CONDITIONS_OPTIONS;
      const response = await getData(requestUrl);
      return response;
    } catch (error) {
      return [];
    }
  };

  // load severity options
  const loadSeverityOptions = async () => {
    try {
      const requestUrl = url.SERVER_URL + url.ALLERGY_SEVERITIES_OPTIONS;
      const response = await getData(requestUrl);
      return response;
    } catch (error) {
      return [];
    }
  };

  //load medication options
  const loadMedicationOptions = async (inputValue) => {
    try {
      const requestUrl =
        url.API_BASE_URL +
        url.API_GET_ALL_PRODUCTS_LIST +
        `?token=${token}&title=${inputValue}`;

      const response = await getData(requestUrl);

      return response.data;
    } catch (error) {
      return [];
    }
  };

  //load lab test options
  const loadLabTestOptions = async (inputValue) => {
    try {
      let requestUrl =
        url.API_BASE_URL +
        url.API_GET_ALL_MEDICAL_TEST +
        `?token=${token}&queryinput=${inputValue}&istemplate=false`;

      if (patientObj) {
        requestUrl += `&members=${patientObj?.value}`;
      }

      const response = await getData(requestUrl);
      return response.data;
    } catch (error) {
      return [];
    }
  };

  // File upload handler
  const uploadHandler = async (files) => {
    try {
      setDocFileUploading(true);
      let requestUrl =
        url.API_BASE_URL + url.API_SINGLE_FILE_UPLOAD + `?token=${token}`;
      const response = await uploadSingleFile(requestUrl, files[0]);
      setDocFileUploading(false);
      if (response.status) {
        setFileUploadId(response.data._id);
        setUploadedFile(response.data);
      }
    } catch (error) {
      console.error(error.message);
    }
  };

  // Remove uploaded file
  const closeDocFileHandler = () => {
    setFileUploadId(null);
    setUploadedFile({});
  };

  // Disease handlers
  const handleDiseaseInput = (field, value) => {
    setDiseaseForm({ ...diseaseForm, [field]: value });
  };

  // Add disease
  const handleAddDisease = () => {
    if (!validateDiseaseForm()) {
      return;
    }
    if (editDiseaseIdx !== null) {
      const updated = [...diseasesList];
      updated[editDiseaseIdx] = diseaseForm;
      setDiseasesList(updated);
      setEditDiseaseIdx(null);
    } else {
      setDiseasesList([...diseasesList, diseaseForm]);
    }
    setDiseaseForm({
      disease: "",
      name: "",
      severity: "",
      duration: "",
      description: "",
    });
    setDiseaseFormErrors({});
  };

  // Edit disease
  const handleEditDisease = (idx) => {
    setDiseaseForm(diseasesList[idx]);
    setEditDiseaseIdx(idx);
  };

  // Delete disease
  const handleDeleteDisease = (idx) => {
    setDiseasesList(diseasesList.filter((_, i) => i !== idx));
    if (editDiseaseIdx === idx) {
      setDiseaseForm({
        disease: "",
        name: "",
        severity: "",
        duration: "",
        description: "",
      });
      setEditDiseaseIdx(null);
    }
  };

  // Medication handlers
  const handleMedicationInput = (field, value) => {
    setMedicationForm({ ...medicationForm, [field]: value });
  };

  // Add medication
  const handleAddMedication = () => {
    if (!validateMedicationForm()) {
      return;
    }
    if (editMedicationIdx !== null) {
      const updated = [...medicationsList];
      updated[editMedicationIdx] = medicationForm;
      setMedicationsList(updated);
      setEditMedicationIdx(null);
    } else {
      setMedicationsList([...medicationsList, medicationForm]);
    }
    setMedicationForm({
      medicine: null,
      medicinevalue: null,
      name: "",
      dose: "",
      frequency: "",
      duration: "",
      purpose: "",
    });
    setMedicationFormErrors({});
  };

  // Edit medication
  const handleEditMedication = (idx) => {
    setMedicationForm(medicationsList[idx]);
    setEditMedicationIdx(idx);
  };

  // Delete medication
  const handleDeleteMedication = (idx) => {
    setMedicationsList(medicationsList.filter((_, i) => i !== idx));
    if (editMedicationIdx === idx) {
      setMedicationForm({
        medicine: "",
        name: "",
        dose: "",
        frequency: "",
        duration: "",
        purpose: "",
      });
      setEditMedicationIdx(null);
    }
  };

  // Laboratory test handlers
  const handleLabTestInput = (field, value) => {
    setLabTestForm({ ...labTestForm, [field]: value });
  };

  // Add laboratory test
  const handleAddLabTest = () => {
    if (!validateLabTestForm()) {
      return;
    }
    if (editLabTestIdx !== null) {
      const updated = [...laboratoryTestList];
      updated[editLabTestIdx] = labTestForm;
      setLaboratoryTestList(updated);
      setEditLabTestIdx(null);
    } else {
      setLaboratoryTestList([...laboratoryTestList, labTestForm]);
    }
    setLabTestForm({
      medicaltest: null,
      medicaltestvalue: null,
      name: "",
      result: "",
      date: todayValue,
    });
    setLabTestFormErrors({});
  };

  // Edit laboratory test
  const handleEditLabTest = (idx) => {
    setLabTestForm(laboratoryTestList[idx]);
    setEditLabTestIdx(idx);
  };

  // Delete laboratory test
  const handleDeleteLabTest = (idx) => {
    setLaboratoryTestList(laboratoryTestList.filter((_, i) => i !== idx));
    if (editLabTestIdx === idx) {
      setLabTestForm({ medicaltest: "", name: "", result: "", date: "" });
      setEditLabTestIdx(null);
    }
  };

  // Fetch prescription details
  const getPrescriptionDetails = async () => {
    try {
      setIsLoadingDetails(true);
      let requestUrl =
        url.API_BASE_URL +
        url.API_GET_MEDICAL_DOCUMENT_BY_ID +
        `/${selectedPrescriptionId}?token=${token}`;

      console.log("requestUrl in get prescription details ", requestUrl);

      const response = await getData(requestUrl);

      console.log("response in get prescription details ", response);

      if (response.status) {
        const prescriptionData = response.data;
        setDocumentName(prescriptionData.name);
        setTestDate(
          prescriptionData.testdate
            ? new Date(prescriptionData.testdate).toISOString().split("T")[0]
            : ""
        );
        setResultDate(
          prescriptionData.resultdate
            ? new Date(prescriptionData.resultdate).toISOString().split("T")[0]
            : ""
        );
        setSymptoms(prescriptionData.symptoms);
        setSuggestions(prescriptionData.suggestions);
        setComments(prescriptionData.comments);
        if (prescriptionData.assigntodetails) {
          setAssignTo(prescriptionData.assigntodetails);
        }
        if (prescriptionData.doctordetails) {
          setDoctor(prescriptionData.doctordetails);
        }
        if (prescriptionData.clinicdetails) {
          setClinic(prescriptionData.clinicdetails);
        }
        if (prescriptionData.currentstatus) {
          setCurrentStatus({
            value: prescriptionData.currentstatus,
            label: prescriptionData.currentstatus,
          });
        }

        if (prescriptionData.uploadedfileobject) {
          setUploadedFile(prescriptionData?.uploadedfileobject);
          setFileUploadId(prescriptionData?.uploadedfileobject?._id);
        }

        setDiseasesList(prescriptionData.diseases || []);
        setMedicationsList(prescriptionData.medications || []);
        setLaboratoryTestList(prescriptionData.laboratorytests || []);
      }
      setIsLoadingDetails(false);
    } catch (error) {
      console.log(error.message);
      setIsLoadingDetails(false);
    }
  };

  // Save prescription handler
  const savePrescriptionHandler = async () => {
    if (!validateForm()) {
      return;
    }

    const medications = medicationsList.map((item) => {
      return {
        medicine: item.medicine,
        name: item.name,
        dose: item.dose,
        frequency: item.frequency,
        duration: item.duration,
        purpose: item.purpose,
      };
    });

    const laboratorytests = laboratoryTestList.map((item) => {
      return {
        medicaltest: item.medicaltest,
        name: item.name,
        result: item.result,
        date: item.date,
      };
    });

    try {
      setIsSaving(true);
      const apiData = {
        name: documentName,
        testdate: testDate,
        resultdate: resultDate,
        symptoms: symptoms,
        suggestions: suggestions,
        comments: comments,
        assignto: patientObj
          ? patientObj.value || patientObj.id
          : assignTo
          ? assignTo?.value
          : null,
        doctorid: doctor ? doctor?.value : null,
        clinicid: clinic ? clinic?.value : null,
        uploadid: fileUploadId,
        currentstatus: currentStatus ? currentStatus.value : "",
        diseases: diseasesList,
        medications,
        laboratorytests,
      };

      // console.log("apiData", apiData);

      let requestUrl = url.API_BASE_URL;
      let response = {};

      if (selectedPrescriptionId) {
        requestUrl +=
          url.API_UPDATE_MEDICAL_DOCUMENT +
          `/${selectedPrescriptionId}?token=${token}`;
        response = await putData(requestUrl, apiData);
      } else {
        requestUrl += url.API_ADD_MEDICAL_DOCUMENT + `?token=${token}`;
        response = await postData(requestUrl, apiData);
      }

      setIsSaving(false);

      if (response.status) {
        bootstrap.Offcanvas.getInstance(
          document.querySelector("#savePrescriptionOffCanvas")
        ).hide();
        resetHandler();
        afterActionClose([response.data]);
      }
    } catch (error) {
      console.log(error.message);
    }
  };

  // Reset form handler
  const resetHandler = () => {
    setDocumentName("");
    setFileUploadId(null);
    setUploadedFile({});
    setTestDate(new Date().toISOString().split("T")[0]);
    setResultDate(new Date().toISOString().split("T")[0]);
    setSymptoms("");
    setSuggestions("");
    setComments("");
    setAssignTo(patientObj);
    setDoctor(doctorObj);
    setClinic(clinicObj);
    setCurrentStatus(null);
    setErrors({});
    setDiseasesList([]);
    setMedicationsList([]);
    setLaboratoryTestList([]);
    setDiseaseForm({
      disease: "",
      name: "",
      severity: "",
      duration: "",
      description: "",
    });
    setMedicationForm({
      medicine: null,
      medicinevalue: null,
      name: "",
      dose: "",
      frequency: "",
      duration: "",
      purpose: "",
    });
    setLabTestForm({
      medicaltest: null,
      medicaltestvalue: null,
      name: "",
      result: "",
      date: todayValue,
    });
    setEditDiseaseIdx(null);
    setEditMedicationIdx(null);
    setEditLabTestIdx(null);
    setSelectedPrescriptionId(null);
    setMedicationFormErrors({});
    setDiseaseFormErrors({});
    setLabTestFormErrors({});
  };

  // Add these states for validation errors
  const [medicationFormErrors, setMedicationFormErrors] = useState({});
  const [diseaseFormErrors, setDiseaseFormErrors] = useState({});
  const [labTestFormErrors, setLabTestFormErrors] = useState({});

  // Medication validation
  const validateMedicationForm = () => {
    const errors = {};
    if (!medicationForm.medicine || !medicationForm.medicinevalue) {
      errors.medicine = "Medicine is required.";
    }
    if (!medicationForm.name.trim()) {
      errors.name = "Name is required.";
    }
    if (!medicationForm.dose.trim()) {
      errors.dose = "Dose is required.";
    }
    if (!medicationForm.frequency.trim()) {
      errors.frequency = "Frequency is required.";
    }
    if (!medicationForm.duration.trim()) {
      errors.duration = "Duration is required.";
    }
    if (!medicationForm.purpose.trim()) {
      errors.purpose = "Purpose is required.";
    }
    setMedicationFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Disease validation
  const validateDiseaseForm = () => {
    const errors = {};
    if (!diseaseForm.disease) {
      errors.disease = "Disease is required.";
    }
    if (!diseaseForm.name.trim()) {
      errors.name = "Name is required.";
    }
    if (!diseaseForm.severity) {
      errors.severity = "Severity is required.";
    }
    if (!diseaseForm.duration.trim()) {
      errors.duration = "Duration is required.";
    }
    if (!diseaseForm.description.trim()) {
      errors.description = "Description is required.";
    }
    setDiseaseFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Lab Test validation
  const validateLabTestForm = () => {
    const errors = {};
    if (!labTestForm.medicaltest || !labTestForm.medicaltestvalue) {
      errors.medicaltest = "Medical Test is required.";
    }
    if (!labTestForm.name.trim()) {
      errors.name = "Name is required.";
    }
    if (!labTestForm.result.trim()) {
      errors.result = "Result is required.";
    }
    if (!labTestForm.date) {
      errors.date = "Date is required.";
    }
    setLabTestFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Fetch prescription details
  useEffect(() => {
    if (selectedPrescriptionId) {
      getPrescriptionDetails();
    }
  }, [selectedPrescriptionId]);

  useEffect(() => {
    if (patientObj && !selectedPrescriptionId) {
      setAssignTo(patientObj);
    }
  }, [patientObj, selectedPrescriptionId]);

  useEffect(() => {
    if (doctorObj && !selectedPrescriptionId) {
      setDoctor(doctorObj);
    }
  }, [doctorObj, selectedPrescriptionId]);

  useEffect(() => {
    if (clinicObj && !selectedPrescriptionId) {
      setClinic(clinicObj);
    }
  }, [clinicObj, selectedPrescriptionId]);

  return (
    <div
      className="offcanvas lg offcanvas-end bg-white border-0 shadow-lg"
      tabIndex="-1"
      id="savePrescriptionOffCanvas"
      aria-labelledby="savePrescriptionOffCanvas"
    >
      {/* Header */}
      <div className="offcanvas-header bg-primary text-white p-4 pb-3 border-bottom border-gray-300">
        <div className="d-flex align-items-center gap-3">
          <div className="icon-wrapper p-3 bg-white rounded-circle d-flex align-items-center justify-content-center">
            <span className="material-symbols-outlined text-primary fs-5 icon-md">
              description
            </span>
          </div>
          <div>
            <h3 className="offcanvas-title fw-bold text-white mb-0">
              Save Prescription
            </h3>
            <p className="text-white fs-sm mb-0">
              Add or update prescription details
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn-close p-2 bg-gray-200 rounded-circle shadow-sm border-0"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          onClick={resetHandler}
        ></button>
      </div>
      <div className="offcanvas-body p-4">
        <div className="p-3 p-md-4 border border-gray-300 rounded-10 shadow-sm bg-light">
          {isLoadingDetails ? (
            <div
              className="d-flex flex-column align-items-center justify-content-center"
              style={{ minHeight: "300px" }}
            >
              <div
                className="spinner-border text-primary"
                style={{ width: "3rem", height: "3rem" }}
                role="status"
              >
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="mt-3 fw-semibold text-primary fs-5">
                Loading details...
              </div>
            </div>
          ) : (
            <form
              ref={formRef}
              onSubmit={(e) => e.preventDefault()}
              className="d-flex flex-column"
            >
              {/* Basic Details Section */}
              <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                <h5 className="mb-3 fw-semibold">{t("Basic Details")}</h5>
                <div className="row">
                  <div className="col-md-6">
                    <label
                      htmlFor="assignTo"
                      className="d-block fs-sm fw-semibold mb-2 text-dark"
                    >
                      Patient <span className="text-danger">*</span>
                    </label>
                    <AsyncSelect
                      id="assignTo"
                      loadOptions={loadAssineeOptions}
                      defaultOptions={false}
                      value={assignTo}
                      onChange={(option) => {
                        setAssignTo(option);
                      }}
                      isClearable
                      placeholder="Search or select patient..."
                      styles={reactSelectStyle("3px", "6px", true, false)}
                    />

                    {assignTo ? (
                      <PatientDetailsCard patient={assignTo} />
                    ) : null}
                  </div>
                  <div className="col-md-6">
                    <label
                      htmlFor="doctor"
                      className="d-block fs-sm fw-semibold mb-2 text-dark"
                    >
                      Doctor <span className="text-danger">*</span>
                    </label>
                    <AsyncSelect
                      id="doctor"
                      loadOptions={loadDoctorOptions}
                      defaultOptions={false}
                      cacheOptions={false}
                      value={doctor}
                      onChange={(option) => {
                        setDoctor(option);
                      }}
                      isClearable
                      placeholder="Search or select doctor..."
                      styles={reactSelectStyle("3px", "6px", true, false)}
                    />

                    {doctor ? <DoctorDetailsCard doctor={doctor} /> : null}
                  </div>
                </div>

                <div className="row mt-2">
                  <div className="col-md-6">
                    <label
                      htmlFor="clinic"
                      className="d-block fs-sm fw-semibold mb-2 text-dark"
                    >
                      Clinic
                    </label>
                    <AsyncSelect
                      id="clinic"
                      loadOptions={loadClinicOptions}
                      defaultOptions={false}
                      cacheOptions={false}
                      value={clinic}
                      onChange={(option) => {
                        setClinic(option);
                      }}
                      isClearable
                      placeholder="Search or select clinic..."
                      styles={reactSelectStyle("3px", "6px", true, false)}
                    />

                    {clinic ? <ClinicDetailsCard clinic={clinic} /> : null}
                  </div>
                </div>
              </div>

              {/* Medical Condition Section */}
              <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                <h5 className="mb-3 fw-semibold">
                  {t("Reported Medical Condition")}
                </h5>
                <div className="fields-container flex-fill">
                  {/* Document Name and Current Status */}
                  <div className="row mb-4">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label
                          htmlFor="documentName"
                          className="d-block fs-sm fw-semibold mb-2 text-dark"
                        >
                          Condition <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          id="documentName"
                          className={`form-control fs-sm shadow-none ${
                            errors.documentName
                              ? "is-invalid"
                              : "border border-gray-300"
                          }`}
                          placeholder="e.g., Blood Test Report"
                          value={documentName}
                          onChange={(e) => {
                            setDocumentName(e.target.value);
                            if (errors.documentName)
                              setErrors((prev) => ({
                                ...prev,
                                documentName: "",
                              }));
                          }}
                          aria-describedby="documentNameFeedback"
                        />
                        {errors.documentName && (
                          <div
                            id="documentNameFeedback"
                            className="invalid-feedback"
                          >
                            {errors.documentName}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label
                          htmlFor="currentStatus"
                          className="d-block fs-sm fw-semibold mb-2 text-dark"
                        >
                          Current Status
                        </label>
                        <AsyncSelect
                          id="currentStatus"
                          loadOptions={loadCurrentStatusOptions}
                          defaultOptions={true}
                          value={currentStatus}
                          onChange={setCurrentStatus}
                          isClearable
                          placeholder="Search or select status..."
                          styles={reactSelectStyle("3px", "6px", true, false)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row mb-4">
                    {/* Test Date */}
                    <div className="col-md-6">
                      <label
                        htmlFor="testDate"
                        className="d-block fs-sm fw-semibold mb-2 text-dark"
                      >
                        Reported Date / Visit Date{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        id="testDate"
                        className={`form-control fs-sm shadow-none ${
                          errors.testDate
                            ? "is-invalid"
                            : "border border-gray-300"
                        }`}
                        value={testDate}
                        onChange={(e) => {
                          setTestDate(e.target.value);
                          if (errors.testDate)
                            setErrors((prev) => ({ ...prev, testDate: "" }));
                        }}
                        aria-describedby="testDateFeedback"
                      />
                      {errors.testDate && (
                        <div id="testDateFeedback" className="invalid-feedback">
                          {errors.testDate}
                        </div>
                      )}
                    </div>

                    {/* Result Date */}
                    <div className="col-md-6">
                      <label
                        htmlFor="resultDate"
                        className="d-block fs-sm fw-semibold mb-2 text-dark"
                      >
                        Resolved Date (if curred or resolved)
                      </label>
                      <input
                        type="date"
                        id="resultDate"
                        className="form-control fs-sm shadow-none border border-gray-300"
                        value={resultDate}
                        onChange={(e) => setResultDate(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Comments */}
                  <div className="form-group mb-4">
                    <label
                      htmlFor="comments"
                      className="d-block fs-sm fw-semibold mb-2 text-dark"
                    >
                      Comments
                    </label>
                    <textarea
                      id="comments"
                      className="form-control fs-sm shadow-none border border-gray-300"
                      rows={3}
                      placeholder="Additional notes..."
                      value={comments}
                      onChange={(e) => setComments(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Symptoms and Suggestions Section */}
              <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                <h5 className="mb-3 fw-semibold">
                  {t("Symptoms and Suggestions")}
                </h5>
                <div className="fields-container flex-fill">
                  {/* Symptoms */}
                  <div className="form-group mb-4">
                    <label
                      htmlFor="symptoms"
                      className="d-block fs-sm fw-semibold mb-2 text-dark"
                    >
                      Symptoms
                    </label>
                    <textarea
                      id="symptoms"
                      className="form-control fs-sm shadow-none border border-gray-300"
                      rows={3}
                      placeholder="Describe symptoms..."
                      value={symptoms}
                      onChange={(e) => setSymptoms(e.target.value)}
                    />
                  </div>

                  {/* Suggestions */}
                  <div className="form-group mb-4">
                    <label
                      htmlFor="suggestions"
                      className="d-block fs-sm fw-semibold mb-2 text-dark"
                    >
                      Suggestions
                    </label>
                    <textarea
                      id="suggestions"
                      className="form-control fs-sm shadow-none border border-gray-300"
                      rows={3}
                      placeholder="Provide suggestions..."
                      value={suggestions}
                      onChange={(e) => setSuggestions(e.target.value)}
                    />
                  </div>

                  {/* Attachments */}
                  <div className="form-group mb-4">
                    <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                      Attachments
                    </label>
                    <FileUploadField
                      label={t("Upload Documents")}
                      accept="doc mp4 pdf"
                      onUpload={uploadHandler}
                      files={
                        uploadedFile.path
                          ? [
                              {
                                path: url.SERVER_URL + uploadedFile.path,
                                name: uploadedFile.name,
                              },
                            ]
                          : []
                      }
                      onRemove={closeDocFileHandler}
                      loading={docFileUploading}
                      multiple={false}
                      fileType="document"
                    />
                  </div>
                </div>
              </div>

              {/* Diseases Section */}
              <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                <h5 className="mb-3 fw-semibold">Diseases</h5>
                {/* Disease Input Form */}
                <div className="row mb-2">
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                        Disease
                      </label>
                      <AsyncSelect
                        styles={getCustomStyles(diseaseFormErrors.disease)}
                        placeholder="Search or select disease..."
                        loadOptions={loadDiseaseOptions}
                        defaultOptions={true}
                        value={
                          diseaseForm.disease
                            ? {
                                label: diseaseForm.disease,
                                value: diseaseForm.disease,
                              }
                            : null
                        }
                        onChange={(option) => {
                          if (option) {
                            setDiseaseForm((prev) => ({
                              ...prev,
                              disease: option.value,
                              name: option.value,
                            }));
                            if (diseaseFormErrors.disease)
                              setDiseaseFormErrors((prev) => ({
                                ...prev,
                                disease: "",
                                name: "",
                              }));
                          } else {
                            setDiseaseForm((prev) => ({
                              ...prev,
                              disease: "",
                            }));
                          }
                        }}
                        isClearable
                        classNamePrefix={
                          diseaseFormErrors.disease ? "is-invalid" : ""
                        }
                      />
                      {diseaseFormErrors.disease && (
                        <div className="text-danger fs-sm mt-1">
                          {diseaseFormErrors.disease}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                        Name
                      </label>
                      <input
                        type="text"
                        className={`form-control fs-sm shadow-none ${
                          diseaseFormErrors.name
                            ? "is-invalid"
                            : "border border-gray-300"
                        }`}
                        placeholder="Name of the Disease"
                        value={diseaseForm.name}
                        onChange={(e) => {
                          handleDiseaseInput("name", e.target.value);
                          if (diseaseFormErrors.name)
                            setDiseaseFormErrors((prev) => ({
                              ...prev,
                              name: "",
                            }));
                        }}
                      />
                      {diseaseFormErrors.name && (
                        <div className="invalid-feedback">
                          {diseaseFormErrors.name}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="row mb-2">
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                        Severity
                      </label>
                      <AsyncSelect
                        styles={getCustomStyles(diseaseFormErrors.severity)}
                        placeholder="Search or select severity..."
                        loadOptions={loadSeverityOptions}
                        defaultOptions={true}
                        value={
                          diseaseForm.severity
                            ? {
                                label: diseaseForm.severity,
                                value: diseaseForm.severity,
                              }
                            : null
                        }
                        onChange={(option) => {
                          handleDiseaseInput("severity", option?.value);
                          if (diseaseFormErrors.severity)
                            setDiseaseFormErrors((prev) => ({
                              ...prev,
                              severity: "",
                            }));
                        }}
                        isClearable
                        classNamePrefix={
                          diseaseFormErrors.severity ? "is-invalid" : ""
                        }
                      />
                      {diseaseFormErrors.severity && (
                        <div className="text-danger fs-sm mt-1">
                          {diseaseFormErrors.severity}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                        Duration
                      </label>
                      <input
                        type="text"
                        className={`form-control fs-sm shadow-none ${
                          diseaseFormErrors.duration
                            ? "is-invalid"
                            : "border border-gray-300"
                        }`}
                        placeholder="E.g., 1 week, 2 months, etc."
                        value={diseaseForm.duration}
                        onChange={(e) => {
                          handleDiseaseInput("duration", e.target.value);
                          if (diseaseFormErrors.duration)
                            setDiseaseFormErrors((prev) => ({
                              ...prev,
                              duration: "",
                            }));
                        }}
                      />
                      {diseaseFormErrors.duration && (
                        <div className="invalid-feedback">
                          {diseaseFormErrors.duration}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="row mb-2">
                  <div className="col-md-12">
                    <div className="form-group mb-3">
                      <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                        Description
                      </label>
                      <textarea
                        rows="3"
                        className={`form-control fs-sm shadow-none ${
                          diseaseFormErrors.description
                            ? "is-invalid"
                            : "border border-gray-300"
                        }`}
                        placeholder="Description of the Disease"
                        value={diseaseForm.description}
                        onChange={(e) => {
                          handleDiseaseInput("description", e.target.value);
                          if (diseaseFormErrors.description)
                            setDiseaseFormErrors((prev) => ({
                              ...prev,
                              description: "",
                            }));
                        }}
                      ></textarea>
                      {diseaseFormErrors.description && (
                        <div className="invalid-feedback">
                          {diseaseFormErrors.description}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="row mb-2">
                  <div className="col-md-12">
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={handleAddDisease}
                    >
                      {editDiseaseIdx !== null
                        ? "Update Disease"
                        : "Add Disease"}
                    </button>
                  </div>
                </div>
                {/* Disease Table */}
                {diseasesList.length > 0 && (
                  <div className="table-wrapper">
                    <div className="table-responsive mb-1">
                      <table className="table align-middle fs-sm text-nowrap border-0 mb-0">
                        <thead>
                          <tr>
                            <th
                              style={{ width: "18%" }}
                              className="bg-transparent fs-md fw-semibold border-0"
                            >
                              Disease Name
                            </th>
                            <th
                              style={{ width: "14%" }}
                              className="bg-transparent fs-md fw-semibold border-0"
                            >
                              Severity
                            </th>
                            <th
                              style={{ width: "14%" }}
                              className="bg-transparent fs-md fw-semibold border-0"
                            >
                              Duration
                            </th>
                            <th
                              style={{ width: "34%" }}
                              className="bg-transparent fs-md fw-semibold border-0"
                            >
                              Description
                            </th>
                            <th
                              style={{ width: "8%" }}
                              className="bg-transparent fs-md fw-semibold border-0 text-center"
                            ></th>
                          </tr>
                        </thead>
                        <tbody>
                          {diseasesList.map((d, idx) => (
                            <tr key={idx} style={{ verticalAlign: "middle" }}>
                              <td style={{ width: "18%" }}>
                                <div className="bg-white border border-gray-300 rounded-2 p-2">
                                  {d.name}
                                </div>
                              </td>
                              <td style={{ width: "14%" }}>
                                <div className="bg-white border border-gray-300 rounded-2 p-2">
                                  {d.severity}
                                </div>
                              </td>
                              <td style={{ width: "14%" }}>
                                <div className="bg-white border border-gray-300 rounded-2 p-2">
                                  {d.duration}
                                </div>
                              </td>
                              <td style={{ width: "34%" }}>
                                <div className="bg-white border border-gray-300 rounded-2 p-2">
                                  {d.description}
                                </div>
                              </td>
                              <td
                                className="text-center"
                                style={{ width: "8%" }}
                              >
                                <div className="d-flex justify-content-center align-items-center gap-2">
                                  <button
                                    type="button"
                                    className="btn btn-link p-0 text-edit"
                                    onClick={() => handleEditDisease(idx)}
                                    title="Edit"
                                    style={{ color: "#57595bff" }}
                                  >
                                    <span
                                      className="material-symbols-outlined"
                                      style={{ fontSize: "22px" }}
                                    >
                                      edit
                                    </span>
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-link p-0 text-delete"
                                    onClick={() => handleDeleteDisease(idx)}
                                    title="Delete"
                                    style={{ color: "#d32f2f" }}
                                  >
                                    <span
                                      className="material-symbols-outlined"
                                      style={{ fontSize: "22px" }}
                                    >
                                      delete
                                    </span>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {/* Medications Section */}
              <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                <h5 className="mb-3 fw-semibold">Medications</h5>
                {/* Medication Input Form */}
                <div className="row mb-2">
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                        Medicine
                      </label>
                      <AsyncSelect
                        styles={getCustomStyles(medicationFormErrors.medicine)}
                        placeholder="Search or select medicine..."
                        loadOptions={loadMedicationOptions}
                        defaultOptions={false}
                        value={
                          medicationForm.medicinevalue
                            ? medicationForm.medicinevalue
                            : null
                        }
                        onChange={(option) => {
                          if (option) {
                            setMedicationForm((prev) => ({
                              ...prev,
                              medicine: option.value,
                              medicinevalue: option,
                              name: option.label,
                            }));
                            if (medicationFormErrors.medicine)
                              setMedicationFormErrors((prev) => ({
                                ...prev,
                                medicine: "",
                                name: "",
                              }));
                          } else {
                            setMedicationForm((prev) => ({
                              ...prev,
                              medicine: "",
                              medicinevalue: null,
                            }));
                          }
                        }}
                        isClearable
                        classNamePrefix={
                          medicationFormErrors.medicine ? "is-invalid" : ""
                        }
                      />
                      {medicationFormErrors.medicine && (
                        <div className="text-danger fs-sm mt-1">
                          {medicationFormErrors.medicine}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                        Name
                      </label>
                      <input
                        type="text"
                        className={`form-control fs-sm shadow-none ${
                          medicationFormErrors.name
                            ? "is-invalid"
                            : "border border-gray-300"
                        }`}
                        placeholder="Name of the Medicine"
                        value={medicationForm.name}
                        onChange={(e) => {
                          handleMedicationInput("name", e.target.value);
                          if (medicationFormErrors.name)
                            setMedicationFormErrors((prev) => ({
                              ...prev,
                              name: "",
                            }));
                        }}
                      />
                      {medicationFormErrors.name && (
                        <div className="invalid-feedback">
                          {medicationFormErrors.name}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Medicine details card */}
                {medicationForm.medicinevalue ? (
                  <div className="clinic-details mb-3 p-3 border border-gray-300 rounded-8 shadow-sm">
                    {/* Clinic details */}
                    <div className="d-flex gap-3">
                      <div
                        className="avatar rounded-circle overflow-hidden"
                        style={{ width: "60px", height: "60px" }}
                      >
                        <img
                          className="w-100 h-100 object-fit-cover"
                          src={
                            medicationForm.medicinevalue?.productimagepath !==
                            ""
                              ? url.SERVER_URL +
                                medicationForm.medicinevalue?.productimagepath
                              : assetImages.defaultUser
                          }
                          alt="clinic"
                        />
                      </div>
                      <div>
                        <p className="mb-1">
                          Manufacturer:{" "}
                          {medicationForm.medicinevalue?.companyname}
                        </p>
                        <p className="font-sm text-gray mb-1">
                          {medicationForm.medicinevalue?.summary}
                        </p>
                      </div>
                    </div>
                    <div className="contact-info pt-3 border-top border-gray-100 mt-3">
                      {(medicationForm.medicinevalue.weight ||
                        medicationForm.medicinevalue.length ||
                        medicationForm.medicinevalue.width ||
                        medicationForm.medicinevalue.height) && (
                        <div className="mb-1 d-flex flex-wrap gap-3 text-gray">
                          {medicationForm.medicinevalue.weight && (
                            <span className="text-gray">
                              Weight (gm) :{" "}
                              {medicationForm.medicinevalue.weight}
                            </span>
                          )}
                          {medicationForm.medicinevalue.length && (
                            <span className="text-gray">
                              Length (cm) :{" "}
                              {medicationForm.medicinevalue.length}
                            </span>
                          )}
                          {medicationForm.medicinevalue.width && (
                            <span className="text-gray">
                              Width (cm) : {medicationForm.medicinevalue.width}
                            </span>
                          )}
                          {medicationForm.medicinevalue.height && (
                            <span className="text-gray">
                              Height (cm) :{" "}
                              {medicationForm.medicinevalue.height}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}

                <div className="row mb-2">
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                        Dose
                      </label>
                      <input
                        type="text"
                        className={`form-control fs-sm shadow-none ${
                          medicationFormErrors.dose
                            ? "is-invalid"
                            : "border border-gray-300"
                        }`}
                        placeholder="Dose"
                        value={medicationForm.dose}
                        onChange={(e) => {
                          handleMedicationInput("dose", e.target.value);
                          if (medicationFormErrors.dose)
                            setMedicationFormErrors((prev) => ({
                              ...prev,
                              dose: "",
                            }));
                        }}
                      />
                      {medicationFormErrors.dose && (
                        <div className="invalid-feedback">
                          {medicationFormErrors.dose}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                        Frequency
                      </label>
                      <input
                        type="text"
                        className={`form-control fs-sm shadow-none ${
                          medicationFormErrors.frequency
                            ? "is-invalid"
                            : "border border-gray-300"
                        }`}
                        placeholder="Frequency"
                        value={medicationForm.frequency}
                        onChange={(e) => {
                          handleMedicationInput("frequency", e.target.value);
                          if (medicationFormErrors.frequency)
                            setMedicationFormErrors((prev) => ({
                              ...prev,
                              frequency: "",
                            }));
                        }}
                      />
                      {medicationFormErrors.frequency && (
                        <div className="invalid-feedback">
                          {medicationFormErrors.frequency}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="row mb-2">
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                        Duration
                      </label>
                      <input
                        type="text"
                        className={`form-control fs-sm shadow-none ${
                          medicationFormErrors.duration
                            ? "is-invalid"
                            : "border border-gray-300"
                        }`}
                        placeholder="Duration"
                        value={medicationForm.duration}
                        onChange={(e) => {
                          handleMedicationInput("duration", e.target.value);
                          if (medicationFormErrors.duration)
                            setMedicationFormErrors((prev) => ({
                              ...prev,
                              duration: "",
                            }));
                        }}
                      />
                      {medicationFormErrors.duration && (
                        <div className="invalid-feedback">
                          {medicationFormErrors.duration}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                        Purpose
                      </label>
                      <input
                        type="text"
                        className={`form-control fs-sm shadow-none ${
                          medicationFormErrors.purpose
                            ? "is-invalid"
                            : "border border-gray-300"
                        }`}
                        placeholder="Purpose"
                        value={medicationForm.purpose}
                        onChange={(e) => {
                          handleMedicationInput("purpose", e.target.value);
                          if (medicationFormErrors.purpose)
                            setMedicationFormErrors((prev) => ({
                              ...prev,
                              purpose: "",
                            }));
                        }}
                      />
                      {medicationFormErrors.purpose && (
                        <div className="invalid-feedback">
                          {medicationFormErrors.purpose}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="row mb-2">
                  <div className="col-md-12">
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={handleAddMedication}
                    >
                      {editMedicationIdx !== null
                        ? "Update Medication"
                        : "Add Medication"}
                    </button>
                  </div>
                </div>
                {/* Medication Table */}
                {medicationsList.length > 0 && (
                  <div className="table-wrapper">
                    <div className="table-responsive mb-1">
                      <table className="table align-middle fs-sm text-nowrap border-0 mb-0">
                        <thead>
                          <tr>
                            <th
                              style={{ width: "20%" }}
                              className="bg-transparent fs-md fw-semibold border-0"
                            >
                              Medicine Name
                            </th>
                            <th
                              style={{ width: "13%" }}
                              className="bg-transparent fs-md fw-semibold border-0"
                            >
                              Dose
                            </th>
                            <th
                              style={{ width: "13%" }}
                              className="bg-transparent fs-md fw-semibold border-0"
                            >
                              Frequency
                            </th>
                            <th
                              style={{ width: "13%" }}
                              className="bg-transparent fs-md fw-semibold border-0"
                            >
                              Duration
                            </th>
                            <th
                              style={{ width: "13%" }}
                              className="bg-transparent fs-md fw-semibold border-0"
                            >
                              Purpose
                            </th>
                            <th
                              style={{ width: "8%" }}
                              className="bg-transparent fs-md fw-semibold border-0 text-center"
                            ></th>
                          </tr>
                        </thead>
                        <tbody>
                          {medicationsList.map((m, idx) => (
                            <tr key={idx} style={{ verticalAlign: "middle" }}>
                              <td style={{ width: "20%" }}>
                                <div className="bg-white border border-gray-300 rounded-2 p-2">
                                  {m.name}
                                </div>
                              </td>
                              <td style={{ width: "13%" }}>
                                <div className="bg-white border border-gray-300 rounded-2 p-2">
                                  {m.dose}
                                </div>
                              </td>
                              <td style={{ width: "13%" }}>
                                <div className="bg-white border border-gray-300 rounded-2 p-2">
                                  {m.frequency}
                                </div>
                              </td>
                              <td style={{ width: "13%" }}>
                                <div className="bg-white border border-gray-300 rounded-2 p-2">
                                  {m.duration}
                                </div>
                              </td>
                              <td style={{ width: "13%" }}>
                                <div className="bg-white border border-gray-300 rounded-2 p-2">
                                  {m.purpose}
                                </div>
                              </td>
                              <td
                                className="text-center"
                                style={{ width: "8%" }}
                              >
                                <div className="d-flex justify-content-center align-items-center gap-2">
                                  <button
                                    type="button"
                                    className="btn btn-link p-0 text-edit"
                                    onClick={() => handleEditMedication(idx)}
                                    title="Edit"
                                    style={{ color: "#57595bff" }}
                                  >
                                    <span
                                      className="material-symbols-outlined"
                                      style={{ fontSize: "22px" }}
                                    >
                                      edit
                                    </span>
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-link p-0 text-delete"
                                    onClick={() => handleDeleteMedication(idx)}
                                    title="Delete"
                                    style={{ color: "#d32f2f" }}
                                  >
                                    <span
                                      className="material-symbols-outlined"
                                      style={{ fontSize: "22px" }}
                                    >
                                      delete
                                    </span>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {/* Laboratory Tests Section */}
              <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                <h5 className="mb-3 fw-semibold">Laboratory Tests</h5>
                {/* Laboratory Test Input Form */}
                <div className="row mb-2">
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                        Medical Test
                      </label>
                      <AsyncSelect
                        styles={getCustomStyles(labTestFormErrors.medicaltest)}
                        placeholder="Search or select medical test..."
                        loadOptions={loadLabTestOptions}
                        defaultOptions={false}
                        value={
                          labTestForm.medicaltestvalue
                            ? labTestForm.medicaltestvalue
                            : null
                        }
                        onChange={(option) => {
                          if (option) {
                            setLabTestForm((prev) => ({
                              ...prev,
                              medicaltest: option.value,
                              medicaltestvalue: option,
                              name: option.label,
                            }));
                            if (labTestFormErrors.medicaltest)
                              setLabTestFormErrors((prev) => ({
                                ...prev,
                                medicaltest: "",
                                name: "",
                              }));
                          } else {
                            setLabTestForm((prev) => ({
                              ...prev,
                              medicaltest: "",
                              medicaltestvalue: null,
                            }));
                          }
                        }}
                        isClearable
                        classNamePrefix={
                          labTestFormErrors.medicaltest ? "is-invalid" : ""
                        }
                      />
                      {labTestFormErrors.medicaltest && (
                        <div className="text-danger fs-sm mt-1">
                          {labTestFormErrors.medicaltest}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                        Name
                      </label>
                      <input
                        type="text"
                        className={`form-control fs-sm shadow-none ${
                          labTestFormErrors.name
                            ? "is-invalid"
                            : "border border-gray-300"
                        }`}
                        placeholder="Name of the Test"
                        value={labTestForm.name}
                        onChange={(e) => {
                          handleLabTestInput("name", e.target.value);
                          if (labTestFormErrors.name)
                            setLabTestFormErrors((prev) => ({
                              ...prev,
                              name: "",
                            }));
                        }}
                      />
                      {labTestFormErrors.name && (
                        <div className="invalid-feedback">
                          {labTestFormErrors.name}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {/* Lab Test Details Card Row */}
                {labTestForm.medicaltestvalue && (
                  <div className="mb-2">
                    <div className="patient-details mb-3 p-3 border border-gray-300 rounded-8 shadow-md">
                      <div className="mb-3">
                        <strong>Test Name:</strong>{" "}
                        {labTestForm.medicaltestvalue.name || "-"}
                      </div>
                      <div className="mb-3">
                        <strong>Code:</strong>{" "}
                        {labTestForm.medicaltestvalue.code || "-"}
                      </div>
                      <div className="mb-3">
                        <strong>LOINC Code:</strong>{" "}
                        {labTestForm.medicaltestvalue.loinccode || "-"}
                      </div>
                      {/* Patient & Doctor Row */}
                      <div className="row mb-3">
                        {/* Patient */}
                        <div className="col-md-6 d-flex gap-3 align-items-center flex-grow-1">
                          <div
                            className="avatar rounded-circle overflow-hidden"
                            style={{ width: "60px", height: "60px" }}
                          >
                            <img
                              className="w-100 h-100 object-fit-cover"
                              src={
                                labTestForm.medicaltestvalue.assignedpersonimage
                                  ? url.SERVER_URL +
                                    labTestForm.medicaltestvalue
                                      .assignedpersonimage
                                  : assetImages.defaultUser
                              }
                              alt="patient"
                            />
                          </div>
                          <div>
                            <strong>Patient:</strong>{" "}
                            {labTestForm.medicaltestvalue.assignedpersonname ||
                              "-"}
                          </div>
                        </div>
                        {/* Doctor */}
                        <div className="col-md-6 d-flex gap-3 align-items-center flex-grow-1">
                          <div
                            className="avatar rounded-circle overflow-hidden"
                            style={{ width: "60px", height: "60px" }}
                          >
                            <img
                              className="w-100 h-100 object-fit-cover"
                              src={
                                labTestForm.medicaltestvalue.doctorimage
                                  ? url.SERVER_URL +
                                    labTestForm.medicaltestvalue.doctorimage
                                  : assetImages.defaultUser
                              }
                              alt="doctor"
                            />
                          </div>
                          <div>
                            <strong>Doctor:</strong>{" "}
                            {labTestForm.medicaltestvalue.doctorname || "-"}
                          </div>
                        </div>
                      </div>

                      {/* Clinic & Lab Tester Row */}
                      <div className="row mb-1">
                        {/* Clinic */}
                        <div className="col-md-6 d-flex gap-3 align-items-center flex-grow-1">
                          <div
                            className="avatar rounded-circle overflow-hidden"
                            style={{ width: "60px", height: "60px" }}
                          >
                            <img
                              className="w-100 h-100 object-fit-cover"
                              src={
                                labTestForm.medicaltestvalue.clinicimage
                                  ? url.SERVER_URL +
                                    labTestForm.medicaltestvalue.clinicimage
                                  : assetImages.defaultUser
                              }
                              alt="clinic"
                            />
                          </div>
                          <div>
                            <strong>Clinic:</strong>{" "}
                            {labTestForm.medicaltestvalue.clinicname || "-"}
                          </div>
                        </div>
                        {/* Lab Tester */}
                        <div className="col-md-6 d-flex gap-3 align-items-center flex-grow-1">
                          <div
                            className="avatar rounded-circle overflow-hidden"
                            style={{ width: "60px", height: "60px" }}
                          >
                            <img
                              className="w-100 h-100 object-fit-cover"
                              src={
                                labTestForm.medicaltestvalue.providerimage
                                  ? url.SERVER_URL +
                                    labTestForm.medicaltestvalue.providerimage
                                  : assetImages.defaultUser
                              }
                              alt="lab tester"
                            />
                          </div>
                          <div>
                            <strong>Lab Tester:</strong>{" "}
                            {labTestForm.medicaltestvalue.providername || "-"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div className="row mb-2">
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                        Result
                      </label>
                      <input
                        type="text"
                        className={`form-control fs-sm shadow-none ${
                          labTestFormErrors.result
                            ? "is-invalid"
                            : "border border-gray-300"
                        }`}
                        placeholder="Result"
                        value={labTestForm.result}
                        onChange={(e) => {
                          handleLabTestInput("result", e.target.value);
                          if (labTestFormErrors.result)
                            setLabTestFormErrors((prev) => ({
                              ...prev,
                              result: "",
                            }));
                        }}
                      />
                      {labTestFormErrors.result && (
                        <div className="invalid-feedback">
                          {labTestFormErrors.result}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                        Date
                      </label>
                      <input
                        type="date"
                        className={`form-control fs-sm shadow-none ${
                          labTestFormErrors.date
                            ? "is-invalid"
                            : "border border-gray-300"
                        }`}
                        value={labTestForm.date}
                        onChange={(e) => {
                          handleLabTestInput("date", e.target.value);
                          if (labTestFormErrors.date)
                            setLabTestFormErrors((prev) => ({
                              ...prev,
                              date: "",
                            }));
                        }}
                      />
                      {labTestFormErrors.date && (
                        <div className="invalid-feedback">
                          {labTestFormErrors.date}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="row mb-2">
                  <div className="col-md-12">
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={handleAddLabTest}
                    >
                      {editLabTestIdx !== null
                        ? "Update Laboratory Test"
                        : "Add Laboratory Test"}
                    </button>
                  </div>
                </div>

                {/* Laboratory Test Table */}
                {laboratoryTestList.length > 0 && (
                  <div className="table-wrapper">
                    <div className="table-responsive mb-1">
                      <table className="table align-middle fs-sm text-nowrap border-0 mb-0">
                        <thead>
                          <tr>
                            <th
                              style={{ width: "28%" }}
                              className="bg-transparent fs-md fw-semibold border-0"
                            >
                              Medical Test
                            </th>
                            <th
                              style={{ width: "28%" }}
                              className="bg-transparent fs-md fw-semibold border-0"
                            >
                              Result
                            </th>
                            <th
                              style={{ width: "24%" }}
                              className="bg-transparent fs-md fw-semibold border-0"
                            >
                              Date
                            </th>
                            <th
                              style={{ width: "8%" }}
                              className="bg-transparent fs-md fw-semibold border-0 text-center"
                            ></th>
                          </tr>
                        </thead>
                        <tbody>
                          {laboratoryTestList.map((l, idx) => (
                            <tr key={idx} style={{ verticalAlign: "middle" }}>
                              <td style={{ width: "28%" }}>
                                <div className="bg-white border border-gray-300 rounded-2 p-2">
                                  {l.name}
                                </div>
                              </td>
                              <td style={{ width: "28%" }}>
                                <div className="bg-white border border-gray-300 rounded-2 p-2">
                                  {l.result}
                                </div>
                              </td>
                              <td style={{ width: "24%" }}>
                                <div className="bg-white border border-gray-300 rounded-2 p-2">
                                  {l.date}
                                </div>
                              </td>
                              <td
                                className="text-center"
                                style={{ width: "8%" }}
                              >
                                <div className="d-flex justify-content-center align-items-center gap-2">
                                  <button
                                    type="button"
                                    className="btn btn-link p-0 text-edit"
                                    onClick={() => handleEditLabTest(idx)}
                                    title="Edit"
                                    style={{ color: "#57595bff" }}
                                  >
                                    <span
                                      className="material-symbols-outlined"
                                      style={{ fontSize: "22px" }}
                                    >
                                      edit
                                    </span>
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-link p-0 text-delete"
                                    onClick={() => handleDeleteLabTest(idx)}
                                    title="Delete"
                                    style={{ color: "#d32f2f" }}
                                  >
                                    <span
                                      className="material-symbols-outlined"
                                      style={{ fontSize: "22px" }}
                                    >
                                      delete
                                    </span>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {/* Save Button */}
              <div className="action d-flex justify-content-end pt-3 border-top mt-auto">
                <button
                  type="button"
                  className="btn btn-primary d-flex align-items-center gap-2"
                  disabled={isSaving}
                  style={{ cursor: isSaving ? "not-allowed" : "pointer" }}
                  onClick={savePrescriptionHandler}
                >
                  {isSaving ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined me-1">
                        save
                      </span>
                      Save Prescription
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default SavePrescriptionPopup;
